import React, { useContext, useEffect, useState } from 'react'
import { renderRoutes } from 'react-router-config'
import { FuseScrollbars, FuseMessage, FuseDialog, FuseSuspense } from '@fuse'
import { makeStyles } from '@material-ui/styles'
import { useSelector } from 'react-redux'
import ToolbarLayoutMain from './components/ToolbarLayoutMain'
import ToolbarLayoutMainV2 from './v2/components/ToolbarLayoutMain'
import NavbarWrapperLayoutMainV2 from './v2/components/NavbarWrapperLayoutMain'

import FooterLayoutMain from './components/FooterLayoutMain'
import LeftSideLayoutMain from './components/LeftSideLayoutMain'
import RightSideLayoutMain from './components/RightSideLayoutMain'
import NavbarWrapperLayoutMain from './components/NavbarWrapperLayoutMain'
import clsx from 'clsx'
import AppContext from 'app/AppContext'
import useTransfer, { STATUS_REJECTED } from '../../../commons/useTransfer'
import TransferDeclinedDialog from '../../main/settings/transfer/TransferDeclinedDialog'
import { equals, lensPath, view, concat, compose } from 'ramda'
import Leftbar from './v2/components/Leftbar'
import { Hidden } from '@material-ui/core'
import AlertWarning from 'app/fuse-layouts/layout-main/v2/components/AlertWarning'
import { useIsAuthorized } from 'app/auth/authHooks'

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    height: '100%',
    overflow: 'hidden',
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.primary,
    '&.boxed': {
      maxWidth: 1280,
      margin: '0 auto',
      boxShadow: theme.shadows[3],
    },
    '&.scroll-body': {
      '& $wrapper': {
        height: 'auto',
        flex: '0 0 auto',
        overflow: 'auto',
      },
      '& $contentWrapper': {},
      '& $content': {},
    },
    '&.scroll-content': {
      '& $wrapper': {},
      '& $contentWrapper': {},
      '& $content': {},
    },
    '& .navigation': {
      '& .list-subheader-text, & .list-item-text, & .item-badge, & .arrow-icon':
        {
          transition: theme.transitions.create('opacity', {
            duration: theme.transitions.duration.shortest,
            easing: theme.transitions.easing.easeInOut,
          }),
        },
    },
  },
  leffBar: {
    backgroundColor: '#183560',
  },
  notificationContainer: {
    '& p, span, a': {
      color: `${theme.palette.secondary.main} !important`,
    },
  },
  bgNotification: {
    background: theme.palette.grey.veryLight,
    zIndex: '-1',
  },
  wrapper: {
    display: 'flex',
    position: 'relative',
    width: '100%',
    height: '100%',
    flex: '1 1 auto',
  },
  contentWrapper: {
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    zIndex: 3,
    overflow: 'hidden',
    flex: '1 1 auto',
  },
  content: {
    position: 'relative',
    display: 'flex',
    overflow: 'hidden',
    flex: '1 1 auto',
    flexDirection: 'column',
    width: '100%',
    '-webkit-overflow-scrolling': 'touch',
    zIndex: 2,
  },
}))

function LayoutMain(props) {
  const config = useSelector(({ fuse }) => fuse.settings.current.layout.config)
  const isMaintenanceDisabled = useSelector(
    ({ maintenanceConfig }) => maintenanceConfig.isMaintenanceDisable,
  )
  const isAuthorized = useIsAuthorized()

  const appContext = useContext(AppContext)
  const classes = useStyles(props)
  const { routes } = appContext
  const { createIsSeenButton } = useTransfer()
  const [isSeenLoading, setIsSeen] = createIsSeenButton()
  const { selectedPlace } = useSelector(({ auth }) => auth.user)
  const isRejected = equals(
    view(lensPath(['transferRequestTo', 'status']))(selectedPlace),
    STATUS_REJECTED,
  )
  const fullName =
    isRejected &&
    compose(
      concat(
        view(lensPath(['transferRequestTo', 'user', 'firstName']))(
          selectedPlace,
        ),
      ),
      concat(' '),
      concat(
        view(lensPath(['transferRequestTo', 'user', 'lastName']))(
          selectedPlace,
        ),
      ),
    )('')

  const [state, setState] = useState(isRejected)
  const modal = (
    <TransferDeclinedDialog
      isOpen={state}
      onClose={async () => {
        await setIsSeen(isRejected && selectedPlace.transferRequestTo.id)
        setState(false)
      }}
      loading={isSeenLoading}
      teamMemberName={fullName}
    />
  )

  useEffect(() => {
    setState(isRejected)
  }, [isRejected])

  switch (config.scroll) {
    case 'body': {
      return (
        <div
          id="fuse-layout"
          className={clsx(classes.root, config.mode, 'scroll-' + config.scroll)}
        >
          {config.leftSidePanel.display && isMaintenanceDisabled === false && (
            <LeftSideLayoutMain />
          )}

          <div className="flex flex-1 flex-col overflow-hidden relative">
            {config.toolbar.display &&
              config.toolbar.style === 'fixed' &&
              config.toolbar.position === 'above' &&
              isMaintenanceDisabled === false && <ToolbarLayoutMain />}

            <FuseScrollbars
              id="scrollableDiv"
              className="h-full"
              scrollToTopOnChildChange
            >
              {config.toolbar.display &&
                config.toolbar.style !== 'fixed' &&
                config.toolbar.position === 'above' &&
                isMaintenanceDisabled === false && <ToolbarLayoutMain />}

              <div className={classes.wrapper}>
                {config.navbar.display && config.navbar.position === 'left' && (
                  <NavbarWrapperLayoutMain />
                )}

                {config.toolbar.display &&
                  config.toolbar.position === 'below' &&
                  isMaintenanceDisabled === false && <ToolbarLayoutMain />}

                <div className={classes.content}>
                  <FuseDialog />

                  <FuseSuspense>{renderRoutes(routes)}</FuseSuspense>

                  {props.children}
                </div>

                {config.footer.display &&
                  config.footer.position === 'below' && <FooterLayoutMain />}

                {config.navbar.display &&
                  config.navbar.position === 'right' && (
                    <NavbarWrapperLayoutMain />
                  )}
              </div>

              {config.footer.display &&
                config.footer.style !== 'fixed' &&
                config.footer.position === 'above' && <FooterLayoutMain />}
            </FuseScrollbars>

            {config.footer.display &&
              config.footer.style === 'fixed' &&
              config.footer.position === 'above' && <FooterLayoutMain />}
          </div>

          {config.rightSidePanel.display && <RightSideLayoutMain />}

          <FuseMessage />
          {modal}
        </div>
      )
    }

    case 'content':
    default: {
      return (
        <div
          id="fuse-layout"
          className={clsx(classes.root, config.mode, 'scroll-' + config.scroll)}
        >
          <div className="flex flex-1 flex-col overflow-hidden relative">
            <div className={classes.wrapper}>
              {config.navbar.display &&
                config.navbar.position === 'left' &&
                isMaintenanceDisabled === false && (
                  <Hidden mdDown>
                    <Leftbar {...props} />
                  </Hidden>
                )}
              <div className="flex flex-1 flex-col overflow-hidden relative">
                {config.toolbar.display &&
                  config.toolbar.position === 'below' &&
                  config.toolbar.style === 'fixed' &&
                  isMaintenanceDisabled === false && (
                    <ToolbarLayoutMainV2 {...props} />
                  )}

                <div className="flex overflow-hidden h-screen">
                  {config.navbar.display &&
                    config.navbar.position === 'left' &&
                    isMaintenanceDisabled === false && (
                      <NavbarWrapperLayoutMainV2 {...props} />
                    )}
                  <div
                    className={clsx(classes.content, 'flex-1 overflow-hidden')}
                  >
                    {isAuthorized && <AlertWarning {...props} />}
                    <div
                      className={clsx(
                        classes.contentWrapper,
                        classes.contentWrapperWithGradient,
                      )}
                    >
                      <FuseSuspense>{renderRoutes(routes)}</FuseSuspense>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <FuseMessage />
        </div>
      )
    }
  }
}

export default LayoutMain
